/**
 * Overflow-safe numeric display for mobile cards.
 * Uses tabular nums + optional compact mode + title full value.
 */
import type { CSSProperties, ReactNode } from 'react';

type Tone = 'default' | 'green' | 'red' | 'amber' | 'muted' | 'cyan';

const toneClass: Record<Tone, string> = {
  default: '',
  green: 'tc-mob-num--green',
  red: 'tc-mob-num--red',
  amber: 'tc-mob-num--amber',
  muted: 'tc-mob-num--muted',
  cyan: 'tc-mob-num--cyan',
};

export function MobNum({
  children,
  full,
  tone = 'default',
  size = 'md',
  mono = true,
  className = '',
  style,
}: {
  children: ReactNode;
  /** Full value shown on long-press / title tooltip */
  full?: string | number | null;
  tone?: Tone;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  mono?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  const title =
    full === null || full === undefined ? undefined : String(full);

  return (
    <span
      className={[
        'tc-mob-num',
        `tc-mob-num--${size}`,
        mono ? 'tc-mob-num--mono' : '',
        toneClass[tone],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      title={title}
      style={style}
    >
      {children}
    </span>
  );
}

export function MobMetric({
  label,
  value,
  full,
  tone = 'default',
  sub,
}: {
  label: string;
  value: ReactNode;
  full?: string | number | null;
  tone?: Tone;
  sub?: ReactNode;
}) {
  return (
    <div className="tc-mob-metric">
      <div className="tc-mob-metric__label">{label}</div>
      <MobNum full={full ?? undefined} tone={tone} size="md" className="tc-mob-metric__value">
        {value}
      </MobNum>
      {sub != null && <div className="tc-mob-metric__sub">{sub}</div>}
    </div>
  );
}
